import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CloseIcon } from './icons'
import styles from './Modal.module.css'

interface Props {
  title: string
  note?: string
  wide?: boolean
  /**
   * Диалог фиксированной высоты (720 в макете) со списком, который
   * прокручивается внутри. Так в макете у «Кошельков» и «Автовыводов»:
   * высота диалога не прыгает от числа строк, и кнопка «Добавить»
   * всегда на одном месте — по ней целятся не глядя.
   */
  tall?: boolean | undefined
  onClose: () => void
  children?: ReactNode
  /** Кнопки внизу. Порядок на узком экране переворачивается: главное действие ближе к пальцу. */
  actions?: ReactNode
}

/**
 * Диалог. Используется для добавления кошелька, правил автовывода,
 * подтверждений удаления и деталей операции.
 *
 * Фокус уводится внутрь при открытии и возвращается назад при закрытии:
 * иначе после диалога человек с клавиатуры оказывается в начале
 * страницы и заново ищет, где он был.
 */
export function Modal({ title, note, wide, tall, onClose, children, actions }: Props) {
  const boxRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<Element | null>(null)

  /*
    Свежий `onClose` в ссылке, а не в зависимостях эффекта.

    Каждый вызывающий передаёт стрелку прямо в JSX, то есть НОВУЮ
    функцию на каждый рендер. С `[onClose]` в зависимостях эффект
    переигрывался на каждую букву: уборка возвращала фокус назад,
    а новый заход снова уводил его на первый фокусируемый элемент
    диалога — крестик. Человек печатал один символ и вылетал из поля.
  */
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    returnTo.current = document.activeElement

    /*
      Фокус — в первое ПОЛЕ, и только если полей нет — на кнопку.

      Раньше искалось «первое фокусируемое», а первым в разметке идёт
      крестик: диалог добавления кошелька открывался с фокусом на
      закрытии, и человек с клавиатуры первым же пробелом закрывал
      форму вместо заполнения.
    */
    const box = boxRef.current
    const field = box?.querySelector<HTMLElement>('input, select, textarea')
    const fallback = box?.querySelector<HTMLElement>(
      'button, [tabindex]:not([tabindex="-1"])',
    )
    ;(field ?? fallback)?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current()
    }
    document.addEventListener('keydown', onKey)

    // Фон не должен прокручиваться под диалогом: на телефоне это
    // выглядит как будто страница «уехала» и потерялась.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
      if (returnTo.current instanceof HTMLElement) returnTo.current.focus()
    }
    // Один раз на открытие: см. комментарий про closeRef выше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
    Портал в body, а не место в дереве страницы.

    Затемнение стоит `position: fixed`, и «fixed» означает «относительно
    окна» только пока ни у одного предка нет transform/translate.
    Обёртка экрана кабинета (`.view` в AppLayout) въезжает с `translate`
    при каждой смене пути, и на эти мгновения именно она становится
    системой координат диалога: `inset: 0` растягивается на всю высоту
    страницы, а «по центру» оказывается серединой страницы, а не окна.
    Диалог уезжал вниз, страница прыгала к нему. Через портал предков
    с transform у диалога нет вовсе.
  */
  return createPortal(
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        ref={boxRef}
        className={[styles.box, wide ? styles.wide : '', tall ? styles.tall : '']
          .filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.head}>
          <div className={styles.titles}>
            <h2 className={styles.title}>{title}</h2>
            {note ? <span className={styles.note}>{note}</span> : null}
          </div>
          <button type="button" className={styles.close} onClick={onClose}
                  aria-label="Закрыть">
            <CloseIcon size={24} />
          </button>
        </div>

        {children ? <div className={styles.body}>{children}</div> : null}
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Прокручиваемый список внутри диалога.
 *
 * Отступ справа 12px — место под полосу прокрутки, как в макете:
 * без него строка под полосой становится на 12px шире остальных,
 * и правый край списка «дышит» при появлении прокрутки.
 */
export function Scroller({ children }: { children: ReactNode }) {
  return <div className={styles.scroll}>{children}</div>
}
